import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    // Default to dark theme
    const [isDark, setIsDark] = useState(true);

    useEffect(() => {
        // Check for saved preference only
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            setIsDark(savedTheme === 'dark');
        }
        // If no saved preference, keep default (dark)
    }, []);

    useEffect(() => {
        // Apply theme class to body
        document.body.classList.remove('dark', 'light');
        document.body.classList.add(isDark ? 'dark' : 'light');
        localStorage.setItem('theme', isDark ? 'dark' : 'light');
    }, [isDark]);

    const toggleTheme = () => {
        setIsDark(prev => !prev);
    };

    // Premium color palette
    const colors = {
        // Backgrounds
        bg: isDark ? 'bg-transparent' : 'bg-transparent',
        bgPrimary: isDark ? 'bg-black' : 'bg-white',
        bgSecondary: isDark ? 'bg-gray-950' : 'bg-gray-50',
        bgTertiary: isDark ? 'bg-gray-900' : 'bg-gray-100',
        
        // Cards & surfaces
        card: isDark 
            ? 'bg-white/[0.02]' 
            : 'bg-white',
        cardHover: isDark 
            ? 'bg-white/[0.05]' 
            : 'bg-gray-50',
        cardElevated: isDark 
            ? 'bg-gradient-to-br from-white/[0.03] to-white/[0.01]' 
            : 'bg-gradient-to-br from-white to-gray-50/50',
        
        // Borders
        border: isDark 
            ? 'border-white/[0.06]' 
            : 'border-gray-200/80',
        borderLight: isDark 
            ? 'border-white/[0.03]' 
            : 'border-gray-100',
        borderAccent: isDark 
            ? 'border-violet-500/30' 
            : 'border-violet-200',
        
        // Text colors
        text: isDark ? 'text-white' : 'text-gray-900',
        textSecondary: isDark ? 'text-gray-300' : 'text-gray-600',
        textMuted: isDark ? 'text-gray-500' : 'text-gray-400',
        textAccent: isDark ? 'text-violet-400' : 'text-violet-600',
        
        // Interactive elements
        buttonPrimary: isDark
            ? 'bg-gradient-to-r from-violet-500 to-violet-600 text-white hover:from-violet-600 hover:to-violet-700'
            : 'bg-gradient-to-r from-violet-500 to-violet-600 text-white hover:from-violet-600 hover:to-violet-700',
        buttonSecondary: isDark
            ? 'bg-white/[0.05] hover:bg-white/[0.08] text-white border border-white/[0.08] hover:border-white/[0.12]'
            : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-200 hover:border-gray-300',
        buttonGhost: isDark
            ? 'hover:bg-white/[0.05] text-gray-300 hover:text-white'
            : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900',
        
        // Links
        link: isDark 
            ? 'text-violet-400 hover:text-violet-300' 
            : 'text-violet-600 hover:text-violet-700',
        
        // Social/icons
        socialIcon: isDark 
            ? '[&_path]:fill-gray-400 hover:[&_path]:fill-white' 
            : '[&_path]:fill-gray-600 hover:[&_path]:fill-gray-900',
        
        // Special effects
        glow: isDark 
            ? 'shadow-lg shadow-violet-500/20' 
            : 'shadow-lg shadow-violet-500/10',
        glowStrong: isDark 
            ? 'shadow-xl shadow-violet-500/30' 
            : 'shadow-xl shadow-violet-500/20',
        
        // Accent gradients (for text)
        gradientPrimary: 'bg-gradient-to-r from-violet-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent',
        gradientSecondary: 'bg-gradient-to-r from-violet-500 to-cyan-500 bg-clip-text text-transparent',
        
        // Input fields
        input: isDark
            ? 'bg-white/[0.03] border-white/[0.08] text-white placeholder:text-gray-500 focus:border-violet-500/50 focus:ring-violet-500/20'
            : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/20',
        
        // Status colors
        success: isDark ? 'text-emerald-400' : 'text-emerald-600',
        error: isDark ? 'text-red-400' : 'text-red-600',
        warning: isDark ? 'text-amber-400' : 'text-amber-600',
        info: isDark ? 'text-blue-400' : 'text-blue-600',
        
        // Dividers
        divider: isDark 
            ? 'border-white/[0.05]' 
            : 'border-gray-200',
    };

    const value = {
        isDark,
        toggleTheme,
        colors,
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeContext;
